import { useState, useEffect, useMemo } from 'preact/hooks'
import { Loader2, Plus, Trash2, AlertCircle } from 'lucide-preact'
import { authConnectionsApi } from '@/api/authConnections'
import { AuthConnectionModal } from './AuthConnectionModal'
import { ConfirmationModal } from '@/components'
import { useToast } from '@/hooks/useToast'
import { useCompany } from '@/hooks/useCompany'
import { useLookups } from '@/hooks/useLookups'
import { AUTH_TYPE } from '@/types/authConnections'
import type { AuthenticationType, AuthConnectionDto, AddAuthConnectionRequest } from '@/types/authConnections'

interface AuthConnectionsListProps {
  clientCompanyId: string
  authOrgId: string
  allowedDomains?: string[]
  /**
   * Adds domains to the company's allowed list. Supplied by the settings screen, which owns that
   * field; passed through to the modal so an admin can opt in without cancelling out to edit it.
   */
  onAddAllowedDomains?: (domains: string[]) => Promise<void>
  onConnectionsChange?: (hasUsernamePassword: boolean) => void
}

export function AuthConnectionsList({
  clientCompanyId,
  authOrgId,
  allowedDomains = [],
  onAddAllowedDomains,
  onConnectionsChange
}: AuthConnectionsListProps) {
  const [connections, setConnections] = useState<AuthConnectionDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedType, setSelectedType] = useState<string | null>(null) // Using string Key from lookups
  const [connectionToDelete, setConnectionToDelete] = useState<AuthConnectionDto | null>(null)
  
  const { showSuccess, showError } = useToast()
  const { refresh } = useCompany()
  const { getAuthenticationTypes } = useLookups()
  
  const authTypes = getAuthenticationTypes()

  const loadConnections = async (signal?: AbortSignal) => {
    setLoading(true)
    setError(null)
    try {
      const res = await authConnectionsApi.listConnections(clientCompanyId, authOrgId, signal)
      if (res.error) {
        setError(res.error)
        return
      }
      if (res.data) {
        setConnections(res.data)
        // Notify parent if Username/Password is enabled
        const hasUsernamePassword = res.data.some(
          c => c.Type === AUTH_TYPE.UsernamePassword && c.IsEnabled
        )
        onConnectionsChange?.(hasUsernamePassword)
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return
      setError('Failed to load connections')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const abortController = new AbortController()
    loadConnections(abortController.signal)
    return () => abortController.abort()
  }, [clientCompanyId, authOrgId])

  const handleToggle = async (connection: AuthConnectionDto) => {
    if (!connection.ConnectionId) return
    
    // Prevent disabling if it's the last enabled connection
    const enabledCount = connections.filter(c => c.IsEnabled).length
    if (connection.IsEnabled && enabledCount === 1) {
      showError('Cannot Disable', 'At least one authentication method must remain enabled')
      return
    }

    setTogglingId(connection.ConnectionId)
    try {
      const res = connection.IsEnabled
        ? await authConnectionsApi.disableConnection(clientCompanyId, authOrgId, connection.ConnectionId)
        : await authConnectionsApi.enableConnection(clientCompanyId, authOrgId, connection.ConnectionId)

      if (res.error) {
        showError('Toggle Failed', res.error)
        return
      }

      // Update local state
      setConnections(prev =>
        prev.map(c =>
          c.ConnectionId === connection.ConnectionId
            ? { ...c, IsEnabled: !c.IsEnabled }
            : c
        )
      )

      showSuccess(
        connection.IsEnabled ? 'Method Disabled' : 'Method Enabled',
        `${connection.Type} has been ${connection.IsEnabled ? 'disabled' : 'enabled'}`
      )

      // Refresh company data to update hasEnabledAuthConnections in CompanyProvider
      await refresh()

      // Notify parent of changes
      const hasUsernamePassword = connections.some(
        c => c.Type === AUTH_TYPE.UsernamePassword && (c.ConnectionId === connection.ConnectionId ? !c.IsEnabled : c.IsEnabled)
      )
      onConnectionsChange?.(hasUsernamePassword)
    } catch {
      showError('Toggle Failed', 'Failed to update connection status')
    } finally {
      setTogglingId(null)
    }
  }

  const handleDelete = async () => {
    if (!connectionToDelete?.ConnectionId) return
    
    // Prevent deleting if it's the last connection
    if (connections.length === 1) {
      showError('Cannot Delete', 'At least one authentication method must be configured')
      setConnectionToDelete(null)
      return
    }

    // Prevent deleting if it's the last enabled connection
    const enabledCount = connections.filter(c => c.IsEnabled).length
    if (connectionToDelete.IsEnabled && enabledCount === 1) {
      showError('Cannot Delete', 'Cannot delete the last enabled authentication method. Disable it first or enable another method.')
      setConnectionToDelete(null)
      return
    }

    setDeletingId(connectionToDelete.ConnectionId)
    try {
      const res = await authConnectionsApi.deleteConnection(
        clientCompanyId,
        authOrgId,
        connectionToDelete.ConnectionId
      )

      if (res.error) {
        showError('Delete Failed', res.error)
        return
      }

      setConnections(prev => prev.filter(c => c.ConnectionId !== connectionToDelete.ConnectionId))
      showSuccess('Method Deleted', `${connectionToDelete.Type} has been removed`)
      
      // Notify parent of changes
      const hasUsernamePassword = connections.some(
        c => c.Type === AUTH_TYPE.UsernamePassword && c.ConnectionId !== connectionToDelete.ConnectionId
      )
      onConnectionsChange?.(hasUsernamePassword)
    } catch {
      showError('Delete Failed', 'Failed to delete connection')
    } finally {
      setDeletingId(null)
      setConnectionToDelete(null)
    }
  }

  const handleCreate = async (request: AddAuthConnectionRequest) => {
    const res = await authConnectionsApi.createConnection(
      clientCompanyId,
      authOrgId,
      request
    )

    if (res.error) {
      throw new Error(res.error)
    }

    if (res.data) {
      setConnections(prev => [...prev, res.data!])
      showSuccess('Method Added', `${res.data.Type} has been configured`)
      
      // Refresh company data to update hasEnabledAuthConnections in CompanyProvider
      await refresh()
      
      // Notify parent of changes
      const hasUsernamePassword = res.data.Type === AUTH_TYPE.UsernamePassword || 
        connections.some(c => c.Type === AUTH_TYPE.UsernamePassword)
      onConnectionsChange?.(hasUsernamePassword)
    }
  }

  const openAddModal = (type: string) => {
    setSelectedType(type)
    setShowAddModal(true)
  }

  const closeAddModal = () => {
    setShowAddModal(false)
    setSelectedType(null)
  }

  const getConnectionIcon = (type: AuthenticationType) => {
    switch (type) {
      case AUTH_TYPE.GoogleWorkspace:
        return '🏢'
      case AUTH_TYPE.Microsoft:
        return '🪟'
      case AUTH_TYPE.GoogleSocial:
        return '🔓'
      case AUTH_TYPE.UsernamePassword:
        return '🔐'
      default:
        return '🔑'
    }
  }

  /**
   * Domains already routed by an existing connection on this organization.
   *
   * A domain can belong to only one connection: Auth0's Home Realm Discovery resolves an email
   * domain to whichever connection matches first, silently, so a second connection claiming the
   * same domain is created and then never reached (ORB-632). The server refuses that, and the
   * modal uses this to avoid prefilling a domain the server would reject.
   *
   * Read off each connection's own config rather than the company's allowed-domains list — the
   * company list says which domains it owns, this says which are already spoken for.
   */
  const claimedDomains = useMemo(
    () => connections
      .flatMap(c => [
        ...(c.GoogleWorkspace ? [c.GoogleWorkspace.Domain, ...(c.GoogleWorkspace.DomainAliases ?? [])] : []),
        ...(c.Microsoft?.EmailDomains ?? [])
      ])
      // Explicit predicate rather than `filter(Boolean)`: these values are typed as strings but
      // come from stored documents, so the runtime check earns its place — and this keeps the
      // result `string[]` if those DTO types are ever loosened.
      .filter((d): d is string => typeof d === 'string' && d.trim() !== ''),
    [connections]
  )

  const availableTypes = authTypes
    .map(t => t.Key)
    .filter(typeKey => !connections.some(c => c.Type === typeKey))

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium">Failed to load authentication methods</p>
          <p className="text-xs mt-1">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Authentication Methods
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Manage how users authenticate with this organization
          </p>
        </div>
        {availableTypes.length > 0 && (
          <div className="relative group">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Method
            </button>
            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              {availableTypes.map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => openAddModal(type)}
                  className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                >
                  <span>{getConnectionIcon(type)}</span>
                  <span>{type}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {connections.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            No authentication methods configured
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-500">
            Add at least one method to allow users to sign in
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {connections.map(connection => (
            <div
              key={connection.ConnectionId || connection.Type}
              className="flex items-center justify-between p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="text-2xl flex-shrink-0">
                  {getConnectionIcon(connection.Type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-slate-900 dark:text-white">
                      {connection.Type}
                    </h4>
                    {connection.IsEnabled ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                        Enabled
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
                        Disabled
                      </span>
                    )}
                  </div>
                  {connection.Type === AUTH_TYPE.GoogleWorkspace && connection.GoogleWorkspace && (
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      <span className="font-medium">Domain:</span> {connection.GoogleWorkspace.Domain}
                      {connection.GoogleWorkspace.DomainAliases && connection.GoogleWorkspace.DomainAliases.length > 0 && (
                        <span className="ml-2 text-xs">
                          +{connection.GoogleWorkspace.DomainAliases.length} alias{connection.GoogleWorkspace.DomainAliases.length > 1 ? 'es' : ''}
                        </span>
                      )}
                    </div>
                  )}
                  {connection.Type === AUTH_TYPE.Microsoft && connection.Microsoft && (
                    // Both domains carry meaning here, unlike Google where the alias count is a
                    // detail: the tenant domain identifies the Entra tenant, the email domains are
                    // what users sign in with.
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      <span className="font-medium">Tenant:</span> {connection.Microsoft.TenantDomain}
                      {connection.Microsoft.EmailDomains && connection.Microsoft.EmailDomains.length > 0 && (
                        <span className="ml-2 text-xs">
                          {connection.Microsoft.EmailDomains.length} email domain{connection.Microsoft.EmailDomains.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-1">
                    {connection.ConnectionId && (
                      <p className="text-xs text-slate-500 dark:text-slate-500">
                        <span className="font-medium">ID:</span> {connection.ConnectionId}
                      </p>
                    )}
                    {connection.ConfiguredAt && (
                      <p className="text-xs text-slate-500 dark:text-slate-500">
                        Configured {new Date(connection.ConfiguredAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={connection.IsEnabled}
                    onChange={() => handleToggle(connection)}
                    disabled={!connection.ConnectionId || togglingId === connection.ConnectionId}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-green-600 dark:peer-checked:bg-green-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed" />
                </label>
                {connection.ConnectionId && (
                  <button
                    type="button"
                    onClick={() => setConnectionToDelete(connection)}
                    disabled={deletingId === connection.ConnectionId}
                    className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete method"
                  >
                    {deletingId === connection.ConnectionId ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <AuthConnectionModal
        isOpen={showAddModal}
        authType={selectedType}
        typeLabel={selectedType || undefined}
        allowedDomains={allowedDomains}
        claimedDomains={claimedDomains}
        onAddAllowedDomains={onAddAllowedDomains}
        onClose={closeAddModal}
        onCreate={handleCreate}
      />

      <ConfirmationModal
        isOpen={!!connectionToDelete}
        title="Delete Authentication Method?"
        message={`Are you sure you want to remove ${connectionToDelete ? connectionToDelete.Type : 'this method'}? Users will no longer be able to authenticate using this method.`}
        type="danger"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setConnectionToDelete(null)}
        loading={!!deletingId}
      />
    </div>
  )
}
